import React, { useMemo } from "react";
import { HelmetProvider } from "react-helmet-async";
import { RestfulReactProviderProps } from "restful-react";
import { CommonProvider, FeatureFlagsProvider, themeLight, themeDark } from "@coherehealth/common";
import { ThemeProvider } from "@mui/material/styles";
import config from "./api/config";
import { error as logError, warn as logWarning, scrubUrl } from "./logger";
import { UserProvider, User } from "./UserContext";
import QmRestfulProvider from "QmRestfulProvider";

const onError: NonNullable<RestfulReactProviderProps["onError"]> = (error, retry, response) => {
  if (response && error) {
    // The fetch successfully executed, but the server returned us a failure
    const { message, data, status } = error;
    const { url } = response;

    // The reason for splitting these into multiple lines is that it causes
    // different stack traces, and different stack traces cause different errors
    // in sentry. This is sort of a sentry hack.
    if (status === 403) {
      // 403 should never happen for logged-in users: this suggests a bug
      logError(new Error(`HTTP status ${status} from ${scrubUrl(url)}: [${message}] [${JSON.stringify(data)}]`));
    } else if (status === 404) {
      // 404 _may_ happen but may indicate an application issue
      logWarning(`HTTP status ${status} from ${scrubUrl(url)}: [${message}] [${JSON.stringify(data)}]`);
    } else if (status === 500) {
      logError(new Error(`HTTP status ${status} from ${scrubUrl(url)}: [${message}] [${JSON.stringify(data)}]`));
    } else if (status && (status < 200 || status >= 300)) {
      // Handle all non-2xx status codes
      logError(new Error(`HTTP status ${status} from ${scrubUrl(url)}: [${message}] [${JSON.stringify(data)}]`));
    }
  } else {
    // The fetch call itself failed: this will happen if the network is unavailable
    // or if the URL fails to resolve, etc.
    // Generally, these _wouldn't_ be coding errors, but instead
    // network unavailability or connection reset, etc
    logWarning(`error executing fetch: [${JSON.stringify(error)}]`);
  }
};

function AppProviders({
  children,
  getAccessToken,
  getUser,
  theme = "light",
}: {
  children: React.ReactNode;
  getAccessToken: () => Promise<string | undefined>;
  getUser: () => Promise<User | undefined>;
  theme?: "light" | "dark";
}) {
  const userIdPromise = useMemo(async () => {
    const user = await getUser();
    return user?.sub;
  }, [getUser]);
  return (
    <QmRestfulProvider
      requestOptions={async () => ({
        headers: { Authorization: `Bearer ${await getAccessToken()}` },
      })}
      onError={onError}
      base={config.QM_SERVICE_API_URL}
    >
      <FeatureFlagsProvider
        fetchSimpleFlags={async () => {
          const accessToken = await getAccessToken();
          if (accessToken) {
            return fetch(`${config.SERVICE_API_URL}simpleFeatureFlag`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
          }
        }}
        userIdPromise={userIdPromise}
      >
        <HelmetProvider>
          <CommonProvider theme={theme}>
            <UserProvider getUser={getUser} getToken={getAccessToken}>
              <AppThemeProvider>{children}</AppThemeProvider>
            </UserProvider>
          </CommonProvider>
        </HelmetProvider>
      </FeatureFlagsProvider>
    </QmRestfulProvider>
  );
}

interface AppThemeProviderProps {
  theme?: "light" | "dark";
  children: React.ReactNode;
}

const AppThemeProvider = ({ theme = "light", children }: AppThemeProviderProps) => {
  const themeToUse = theme === "dark" ? themeDark : themeLight;
  return <ThemeProvider theme={themeToUse}>{children}</ThemeProvider>;
};

export default AppProviders;
